import { LabelNamesDto } from '../../dtos/label-names.dto';
import type { ILabelNamesPort } from '../../ports/label-names.port';

export interface IResolveLabelNamesQuery {
  /**
   * Resolves the multilingual display name of each label uuid in a single bulk
   * lookup. Uuids with no stored label are simply absent from the result, so
   * callers can apply their own fallback (typically the uuid itself) rather
   * than dropping the entry.
   *
   * @param uuids - label uuids to resolve. An empty list short-circuits with no
   * read.
   * @returns Localized names indexed by label uuid.
   * @throws Propagates any error raised by the port. Callers decide whether to
   * fail or degrade to their fallback.
   */
  execute(uuids: string[]): Promise<LabelNamesDto>;
}

/**
 * A deliberate one-line delegate over `ILabelNamesPort`.
 *
 * It exists only because the effects layer calls use cases and never ports, so
 * the step editor needs a use case to inject. Both halves of the operation live
 * below it — the read in the port's adapter, the grouping in
 * `LabelNameProjector` — and it applies no fallback of its own, leaving that to
 * whichever caller knows what a missing name should read as.
 */
export class ResolveLabelNamesQuery implements IResolveLabelNamesQuery {
  constructor(private readonly labelNamesPort: ILabelNamesPort) {}

  public execute(uuids: string[]): Promise<LabelNamesDto> {
    return this.labelNamesPort.resolve(uuids);
  }
}
